import React, { useEffect } from 'react';
import { sortBy } from 'lodash';
import moment from 'moment-timezone';
import {
  Form,
  FormInstance,
  DateInput,
  SelectInput,
  SelectInputProps,
  FormErrors
} from 'blueprint-form';

import { Row, Col } from '../../../../grid';
import SelectMethod from '../method';
import SelectTime from '../time';
import Timezone from '../timezone';
import { CardBlock } from '../../card';
import Callout from '../../callout';
import { useScheduleForm } from '../../../../contexts/form';
import { GetStartOfDayDelta, GetCurrentTZ } from '../../../../libs/time';
import {
  EpxSurgeryModuleConfig,
  EpxSurgeryPresetModule,
  IEpxSurgeryPreset,
  IEpxSurgerySchedule,
  useGetAccountPresets,
  useGetModuleConfigs,
  useGetSystemPresets
} from '../../../../libs/apps/epxsurgery';

interface EpxSurgeryData {
  method: string;
  language: string;
  surgeryTime: string;
  tz: string;
  type: 'configuration' | 'preset';
  configuration?: string;
  preset?: number;
}

interface EpxSurgeryEditData {
  method: string;
  language: string;
  start: string;
  tz: string;
}

type EpxSurgeryDataRaw = Omit<EpxSurgeryData, 'surgeryTime' | 'preset'> & {
  surgeryDate: Date;
  time: string;
  preset: string;
};

export const EpxSurgeryDefaultData: EpxSurgeryData = {
  method: 'message',
  language: 'en',
  surgeryTime: moment('10:00', 'HH:mm').add(1, 'day').format(),
  tz: GetCurrentTZ(),
  type: 'configuration',
  configuration: 'kneeAndHip',
  preset: null
};

interface EpxSurgeryFormProps {
  setForm?: (form: FormInstance<EpxSurgeryDataRaw>) => void;
  onSubmit?: (data: EpxSurgeryData | EpxSurgeryEditData) => void;
  rawSchedule?: IEpxSurgerySchedule;
  isDisabled?: boolean;
}

const getDayWord = (n: number) => (Math.abs(n) === 1 ? 'Day' : 'Days');

const EpxSurgeryModuleSchedule = ({
  modules,
  moduleConfigs
}: {
  modules: Record<string, EpxSurgeryPresetModule>;
  moduleConfigs: Record<string, EpxSurgeryModuleConfig>;
}): JSX.Element => {
  const { isEditMode } = useScheduleForm();
  if (!modules || !moduleConfigs) {
    return null;
  }
  const entries: EpxSurgeryPresetModule[] = sortBy(
    Object.keys(modules).map((key) => ({
      ...modules[key],
      key: key
    })),
    (entry) => entry.start
  );
  return (
    <>
      <h4>Schedule Configurations</h4>
      <Row className="mb-2">
        <Col xs={6}>
          <b>Module</b>
        </Col>
        <Col xs={6}>
          <b>Start At</b>
        </Col>
        <Col xs={6}>
          <b>End At</b>
        </Col>
        <Col xs={6}>
          <b>Repeat</b>
        </Col>
      </Row>
      {entries.map((entry, i) => {
        const module = moduleConfigs[entry.key];
        let intent = null;
        if (isEditMode) {
          intent = 'success';
          if (!entry.isActive) {
            intent = 'danger';
          }
        }
        return (
          <Row key={i} className="mb-2">
            <Col xs={6}>
              <Callout intent={intent}>
                <b>{module.name}</b>
                {isEditMode && !entry.isActive && <span> (Disabled)</span>}
              </Callout>
            </Col>
            <Col xs={6}>
              <Callout>
                <b>{Math.abs(entry.start)}</b> {getDayWord(entry.start)}{' '}
                {entry.start < 0 ? 'Before' : 'After'}
              </Callout>
            </Col>
            <Col xs={6}>
              <Callout>
                <b>{Math.abs(entry.end)}</b> {getDayWord(entry.end)}{' '}
                {entry.end < 0 ? 'Before' : 'After'}
              </Callout>
            </Col>
            <Col xs={6}>
              <Callout>
                Every <b>{entry.step > 1 ? `${entry.step} ` : ''}</b>
                {getDayWord(entry.step || 1)}
              </Callout>
            </Col>
          </Row>
        );
      })}
    </>
  );
};

const EpxSurgeryForm = ({
  setForm,
  onSubmit: _onSubmit,
  rawSchedule,
  isDisabled
}: EpxSurgeryFormProps): JSX.Element => {
  // Fetch modules, system and account presets
  const modules = useGetModuleConfigs();
  const systemPresets = useGetSystemPresets();
  const systemPresetOptions: SelectInputProps['options'] = [];
  if (systemPresets) {
    const keys = Object.keys(systemPresets);
    for (const key of keys) {
      const preset = systemPresets[key];
      systemPresetOptions.push({
        label: preset.name,
        value: key
      });
    }
  }
  const { presets: accountPresets } = useGetAccountPresets();
  const accountPresetOptions: SelectInputProps['options'] = [
    {
      label: '-- Select Your Preset --',
      value: ''
    }
  ];
  const accountPresetByID: Record<number, IEpxSurgeryPreset> = {};
  if (accountPresets) {
    for (const preset of accountPresets) {
      accountPresetByID[preset.id] = preset;
      accountPresetOptions.push({
        label: preset.name,
        value: preset.id
      });
    }
  }
  // Set up the form
  const { isEditMode } = useScheduleForm();
  const serialize = (data: EpxSurgeryDataRaw): EpxSurgeryData => {
    const preset = parseInt(data.preset, 10);
    const surgeryTime = moment.tz(data.surgeryDate, data.tz);
    const parsed = moment(data.time, 'HH:mm');
    surgeryTime.hour(parsed.hour()).minute(parsed.minute()).startOf('minute');
    return {
      ...data,
      surgeryTime: surgeryTime.format(),
      preset: data.type === 'preset' ? (preset > 0 ? preset : null) : null,
      configuration: data.type === 'configuration' ? data.configuration : null
    };
  };
  const deserialize = (data: EpxSurgeryData): EpxSurgeryDataRaw => {
    const surgeryTime = moment.tz(data.surgeryTime, data.tz);
    return {
      ...data,
      surgeryDate: surgeryTime.toDate(),
      time: surgeryTime.format('HH:mm'),
      preset: data.preset != null ? String(data.preset) : ''
    };
  };
  const defaultData = deserialize(EpxSurgeryDefaultData);
  const form = new FormInstance<EpxSurgeryDataRaw, EpxSurgeryData>(
    defaultData,
    { serialize, deserialize }
  );
  useEffect(() => {
    if (setForm) {
      setForm(form);
    }
  }, []);
  const onSubmit = (data: EpxSurgeryData): void => {
    if (isEditMode) {
      _onSubmit({
        method: data.method,
        language: data.language,
        start: data.surgeryTime,
        tz: data.tz
      });
    } else {
      _onSubmit(data);
    }
  };
  const validate = (data: EpxSurgeryDataRaw): FormErrors => {
    const errors: FormErrors = {};
    if (data.type === 'preset') {
      const preset = parseInt(data.preset, 10);
      if (!(preset > 0)) {
        errors.preset = 'You must select a preset!';
      }
    }
    return errors;
  };
  // In edit mode, only show module schedule
  const scheduleModules: Record<string, EpxSurgeryPresetModule> = {};
  if (rawSchedule) {
    const tz = rawSchedule.tz;
    const surgeryTime = moment.tz(rawSchedule.surgeryDate, tz);
    for (const key of Object.keys(rawSchedule.modules)) {
      const entry = rawSchedule.modules[key];
      const start = moment.tz(entry.start, tz);
      const end = moment.tz(entry.end, tz);
      scheduleModules[key] = {
        key,
        start: GetStartOfDayDelta(surgeryTime, start),
        end: GetStartOfDayDelta(surgeryTime, end),
        step: entry.step,
        isActive: entry.isActive
      };
    }
  }
  return (
    <CardBlock bare>
      <Form
        form={form}
        onSubmit={onSubmit}
        validate={validate}
        disabled={isDisabled}
      >
        {(props) => {
          return (
            <>
              <CardBlock>
                <Row>
                  <Col sm={12}>
                    <SelectMethod />
                  </Col>
                </Row>
              </CardBlock>
              <CardBlock>
                <Row>
                  <Col sm={12}>
                    <DateInput
                      label="Surgery Date"
                      name="surgeryDate"
                      fill
                      disabled={isEditMode}
                    />
                  </Col>
                  <Col sm={12}>
                    <SelectTime label="Message At" name="time" />
                  </Col>
                  <Col sm={12}>
                    <Timezone label="Timezone" name="tz" fill />
                  </Col>
                </Row>
              </CardBlock>
              {!isEditMode && (
                <CardBlock>
                  <Row>
                    <Col sm={12}>
                      <SelectInput
                        label="Preset Group"
                        name="type"
                        options={[
                          {
                            label: 'CareSignal Presets',
                            value: 'configuration'
                          },
                          {
                            label: 'Custom Presets',
                            value: 'preset'
                          }
                        ]}
                        fill
                      />
                    </Col>
                    {props.values.type === 'configuration' && (
                      <Col sm={12}>
                        <SelectInput
                          label="Surgery Type"
                          name="configuration"
                          options={systemPresetOptions}
                          fill
                        />
                      </Col>
                    )}
                    {props.values.type === 'preset' && (
                      <Col sm={12}>
                        <SelectInput
                          label="Surgery Preset"
                          name="preset"
                          options={accountPresetOptions}
                          disabled={accountPresets.length === 0}
                          fill
                        />
                      </Col>
                    )}
                  </Row>
                  {props.values.type === 'configuration' && systemPresets && (
                    <EpxSurgeryModuleSchedule
                      moduleConfigs={modules}
                      modules={
                        systemPresets[props.values.configuration].modules
                      }
                    />
                  )}
                  {props.values.type === 'preset' &&
                    accountPresets &&
                    accountPresetByID[props.values.preset] && (
                      <EpxSurgeryModuleSchedule
                        moduleConfigs={modules}
                        modules={accountPresetByID[props.values.preset].modules}
                      />
                    )}
                </CardBlock>
              )}
              {isEditMode && (
                <CardBlock>
                  <EpxSurgeryModuleSchedule
                    moduleConfigs={modules}
                    modules={scheduleModules}
                  />
                </CardBlock>
              )}
            </>
          );
        }}
      </Form>
    </CardBlock>
  );
};

export default EpxSurgeryForm;
